import { useState, useCallback } from 'react';
import { useAuthContext } from '@contexts/AuthContext';
import { useLanguage } from '@contexts/LanguageContext';
import impersonationService from '@services/impersonationService';
import { useNotification } from '@hooks/useNotification';

export const useImpersonation = () => {
  const [loading, setLoading] = useState(false);
  const { user, checkAuth } = useAuthContext();
  const { t } = useLanguage();
  const notify = useNotification();

  // Contexte d'impersonation depuis l'utilisateur
  const impersonationContext = user?.impersonation || {
    is_impersonating: false,
    original_user_id: null,
    impersonated_user_id: null,
    started_at: null
  };

  /**
   * Démarre une session d'impersonation
   * @param {number} targetUserId - ID de l'utilisateur à impersonner
   */
  const startImpersonation = useCallback(async (targetUserId) => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await impersonationService.startImpersonation(targetUserId);

      // Rafraîchir le contexte d'authentification
      await checkAuth();

      notify.success(t('admin.impersonation.messages.started'));
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('admin.impersonation.messages.start_failed');
      notify.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loading, checkAuth, notify]);

  /**
   * Termine la session d'impersonation
   */
  const endImpersonation = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await impersonationService.endImpersonation();

      // Rafraîchir le contexte d'authentification
      await checkAuth();

      notify.success(t('admin.impersonation.messages.ended'));

      // Rafraîchir la page pour s'assurer que tout l'état est mis à jour
      setTimeout(() => {
        window.location.reload();
      }, 500); // Petit délai pour laisser le toast s'afficher

      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('admin.impersonation.messages.end_failed');
      notify.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loading, checkAuth, notify]);

  return {
    // Contexte d'impersonation
    ...impersonationContext,

    // États
    loading,

    // Actions
    startImpersonation,
    endImpersonation,

    // Permissions
    canImpersonate: user?.role === 'SUPER_ADMIN'
  };
};
